import { useMemo } from 'react';
import type { ChangeEvent } from 'react';
import type { IntakeShellRenderProps } from './IntakeShell.js';

/**
 * wd-2.3.10 — Step 9 "Data migration source" for the prospect-facing intake
 * portal.
 *
 * Fields mirror `intakeStep9Schema` in `packages/config/src/intake-steps.ts`
 * — a `discriminatedUnion('source', [...])` across 5 branches:
 *
 *   'gingr'      → gingrApiKey (≥10), gingrSubdomain (slug), recordCountsConfirmed
 *                  (customers required, pets/invoices/bookings optional),
 *                  loyaltyBackfill enum, sanitizationRules[]
 *   'moego'      → csvBlobRefs[] (≥1, kind ∈ customers/pets/bookings/invoices),
 *                  sanitizationRules[]
 *   'pawloyalty' → csvBlobRefs[] (same kinds as moego), sanitizationRules[]
 *   'csv'        → csvBlobRefs[] (same + 'staff' kind), columnMapping?,
 *                  sanitizationRules[]
 *   'none'       → confirmedNoMigration: true
 *
 * Live CSV upload via a prospect-facing endpoint is deferred to a
 * follow-up ticket (see D2.3.9 handoff note re: D2.2.4-style extension).
 * For v1 this component collects the blob-ref metadata the schema
 * requires — a file picker pre-fills `blobKey` as `pending:<filename>`
 * and `sizeBytes` from `File.size`, and the prospect can override both
 * manually. The D3.3 commit adapter will resolve `pending:` placeholders
 * when the upload endpoint lands. The field shapes never change.
 */

type SourceVariant = 'gingr' | 'moego' | 'pawloyalty' | 'csv' | 'none' | '';

const SOURCE_OPTIONS = [
  { value: 'gingr', label: 'Gingr (API migration)' },
  { value: 'moego', label: 'Moego (CSV export)' },
  { value: 'pawloyalty', label: 'PawLoyalty (CSV export)' },
  { value: 'csv', label: 'Generic CSV upload' },
  { value: 'none', label: 'No migration — start fresh' },
] as const;

const LOYALTY_BACKFILL_OPTIONS = [
  {
    value: 'retro_current_rate',
    label: 'Retroactively award points at our current rate',
  },
  {
    value: 'from_signup_date',
    label: 'Award points from each customer’s signup date',
  },
  { value: 'none', label: 'No loyalty backfill' },
] as const;
type LoyaltyBackfill = (typeof LOYALTY_BACKFILL_OPTIONS)[number]['value'];

const SANITIZATION_SCOPE_OPTIONS = [
  { value: 'all', label: 'All contexts' },
  { value: 'imported_data_and_ui', label: 'Imported data + UI' },
  { value: 'customer_ui', label: 'Customer-facing UI only' },
] as const;
type SanitizationScope = (typeof SANITIZATION_SCOPE_OPTIONS)[number]['value'];

const CSV_KIND_OPTIONS = [
  { value: 'customers', label: 'Customers' },
  { value: 'pets', label: 'Pets' },
  { value: 'bookings', label: 'Bookings' },
  { value: 'invoices', label: 'Invoices' },
] as const;
type NonStaffCsvKind = (typeof CSV_KIND_OPTIONS)[number]['value'];
type CsvKind = NonStaffCsvKind | 'staff';
const CSV_KINDS_WITH_STAFF: ReadonlyArray<{ value: CsvKind; label: string }> = [
  ...CSV_KIND_OPTIONS,
  { value: 'staff', label: 'Staff' },
];

const PATTERN_MAX_LEN = 120;
const REPLACEMENT_MAX_LEN = 120;
const MAX_SANITIZATION_ROWS = 50;
const MAX_BLOB_REF_ROWS = 50;
const MAX_COLUMN_MAPPING_ROWS = 200;

type GingrDraft = {
  gingrApiKey: string;
  gingrSubdomain: string;
  counts: {
    customers: string;
    pets: string;
    invoices: string;
    bookings: string;
  };
  loyaltyBackfill: LoyaltyBackfill | '';
};

type BlobRefRow = {
  kind: CsvKind | '';
  blobKey: string;
  sizeBytes: string;
};

type SanitizationRuleRow = {
  pattern: string;
  replacement: string;
  scope: SanitizationScope;
};

type ColumnMappingRow = { source: string; target: string };

type CsvDraft = {
  blobRefs: BlobRefRow[];
  columnMapping: ColumnMappingRow[];
};

type Step9Draft = {
  source: SourceVariant;
  gingr: GingrDraft;
  moego: { blobRefs: BlobRefRow[] };
  pawloyalty: { blobRefs: BlobRefRow[] };
  csv: CsvDraft;
  sanitizationRules: SanitizationRuleRow[];
  confirmedNoMigration: boolean;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringField(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function numberField(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string' && value.trim() !== '') return value;
  return '';
}

function readSource(value: unknown): SourceVariant {
  if (
    value === 'gingr'
    || value === 'moego'
    || value === 'pawloyalty'
    || value === 'csv'
    || value === 'none'
  ) {
    return value;
  }
  return '';
}

function readLoyaltyBackfill(value: unknown): LoyaltyBackfill | '' {
  return value === 'retro_current_rate'
    || value === 'from_signup_date'
    || value === 'none'
    ? value
    : '';
}

function readScope(value: unknown): SanitizationScope {
  return value === 'imported_data_and_ui' || value === 'customer_ui'
    ? value
    : 'all';
}

function readCsvKind(value: unknown, allowStaff: boolean): CsvKind | '' {
  if (
    value === 'customers'
    || value === 'pets'
    || value === 'bookings'
    || value === 'invoices'
  ) {
    return value;
  }
  if (allowStaff && value === 'staff') return 'staff';
  return '';
}

function readBlobRefRow(value: unknown, allowStaff: boolean): BlobRefRow {
  const row = isRecord(value) ? value : {};
  return {
    kind: readCsvKind(row.kind, allowStaff),
    blobKey: stringField(row.blobKey),
    sizeBytes: numberField(row.sizeBytes),
  };
}

function readBlobRefs(value: unknown, allowStaff: boolean): BlobRefRow[] {
  if (!Array.isArray(value)) return [];
  return value.map((v) => readBlobRefRow(v, allowStaff));
}

function readSanitizationRow(value: unknown): SanitizationRuleRow {
  const row = isRecord(value) ? value : {};
  return {
    pattern: stringField(row.pattern),
    replacement: stringField(row.replacement),
    scope: readScope(row.scope),
  };
}

function readSanitizationRules(value: unknown): SanitizationRuleRow[] {
  if (!Array.isArray(value)) return [];
  return value.map(readSanitizationRow);
}

function readColumnMapping(value: unknown): ColumnMappingRow[] {
  if (!isRecord(value)) return [];
  return Object.entries(value)
    .filter(([, v]) => typeof v === 'string')
    .map(([source, target]) => ({ source, target: target as string }));
}

function readGingr(value: unknown): GingrDraft {
  const g = isRecord(value) ? value : {};
  const counts = isRecord(g.recordCountsConfirmed) ? g.recordCountsConfirmed : {};
  return {
    gingrApiKey: stringField(g.gingrApiKey),
    gingrSubdomain: stringField(g.gingrSubdomain),
    counts: {
      customers: numberField(counts.customers),
      pets: numberField(counts.pets),
      invoices: numberField(counts.invoices),
      bookings: numberField(counts.bookings),
    },
    loyaltyBackfill: readLoyaltyBackfill(g.loyaltyBackfill),
  };
}

function readStep9(draft: Record<string, unknown>): Step9Draft {
  const source = readSource(draft.source);
  return {
    source,
    gingr: readGingr(source === 'gingr' ? draft : {}),
    moego: {
      blobRefs:
        source === 'moego' ? readBlobRefs(draft.csvBlobRefs, false) : [],
    },
    pawloyalty: {
      blobRefs:
        source === 'pawloyalty' ? readBlobRefs(draft.csvBlobRefs, false) : [],
    },
    csv: {
      blobRefs: source === 'csv' ? readBlobRefs(draft.csvBlobRefs, true) : [],
      columnMapping:
        source === 'csv' ? readColumnMapping(draft.columnMapping) : [],
    },
    sanitizationRules:
      source === 'gingr'
      || source === 'moego'
      || source === 'pawloyalty'
      || source === 'csv'
        ? readSanitizationRules(draft.sanitizationRules)
        : [],
    confirmedNoMigration: source === 'none' && draft.confirmedNoMigration === true,
  };
}

function serializeSanitizationRules(
  rows: ReadonlyArray<SanitizationRuleRow>,
): Record<string, unknown>[] {
  return rows
    .filter((r) => r.pattern.trim() !== '')
    .map((r) => ({
      pattern: r.pattern,
      replacement: r.replacement,
      scope: r.scope,
    }));
}

function serializeBlobRefs(
  rows: ReadonlyArray<BlobRefRow>,
): Record<string, unknown>[] {
  return rows
    .filter((r) => r.kind !== '' && r.blobKey.trim() !== '')
    .map((r) => {
      const size = Number(r.sizeBytes);
      return {
        kind: r.kind,
        blobKey: r.blobKey.trim(),
        sizeBytes: Number.isFinite(size) && size > 0 ? size : 0,
      };
    });
}

function serializeColumnMapping(
  rows: ReadonlyArray<ColumnMappingRow>,
): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  let count = 0;
  for (const r of rows) {
    if (r.source.trim() === '') continue;
    out[r.source.trim()] = r.target;
    count += 1;
  }
  return count === 0 ? undefined : out;
}

function serializeCounts(
  counts: GingrDraft['counts'],
): Record<string, number> {
  const out: Record<string, number> = {};
  const customers = Number(counts.customers);
  out.customers = Number.isFinite(customers) && customers >= 0 ? customers : 0;
  for (const key of ['pets', 'invoices', 'bookings'] as const) {
    const val = counts[key];
    if (val.trim() === '') continue;
    const num = Number(val);
    if (!Number.isFinite(num) || num < 0) continue;
    out[key] = num;
  }
  return out;
}

function serializeStep9(next: Step9Draft): Record<string, unknown> {
  if (next.source === '') return {};
  if (next.source === 'none') {
    return {
      source: 'none',
      confirmedNoMigration: next.confirmedNoMigration,
    };
  }
  if (next.source === 'gingr') {
    const out: Record<string, unknown> = {
      source: 'gingr',
      gingrApiKey: next.gingr.gingrApiKey,
      gingrSubdomain: next.gingr.gingrSubdomain,
      recordCountsConfirmed: serializeCounts(next.gingr.counts),
      sanitizationRules: serializeSanitizationRules(next.sanitizationRules),
    };
    if (next.gingr.loyaltyBackfill !== '') {
      out.loyaltyBackfill = next.gingr.loyaltyBackfill;
    }
    return out;
  }
  if (next.source === 'moego') {
    return {
      source: 'moego',
      csvBlobRefs: serializeBlobRefs(next.moego.blobRefs),
      sanitizationRules: serializeSanitizationRules(next.sanitizationRules),
    };
  }
  if (next.source === 'pawloyalty') {
    return {
      source: 'pawloyalty',
      csvBlobRefs: serializeBlobRefs(next.pawloyalty.blobRefs),
      sanitizationRules: serializeSanitizationRules(next.sanitizationRules),
    };
  }
  // 'csv'
  const out: Record<string, unknown> = {
    source: 'csv',
    csvBlobRefs: serializeBlobRefs(next.csv.blobRefs),
    sanitizationRules: serializeSanitizationRules(next.sanitizationRules),
  };
  const mapping = serializeColumnMapping(next.csv.columnMapping);
  if (mapping !== undefined) out.columnMapping = mapping;
  return out;
}

function groupIssuesByPath(
  issues: ReadonlyArray<{ path: string; message: string }>,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const issue of issues) {
    const key = issue.path === '' ? '_root' : issue.path;
    const list = out[key] ?? [];
    list.push(issue.message);
    out[key] = list;
  }
  return out;
}

function fieldErrorId(field: string): string {
  return `intake-step9-${field}-error`.replace(/[^a-z0-9-]/gi, '-');
}

const SLUG_REGEX = /^[a-z0-9-]+$/;

function describeApiKeyError(value: string): string | null {
  if (value.trim() === '') return null;
  return value.length >= 10 ? null : 'API key must be at least 10 characters.';
}

function describeSubdomainError(value: string): string | null {
  if (value.trim() === '') return null;
  return SLUG_REGEX.test(value)
    ? null
    : 'Use lowercase letters, numbers, and hyphens only.';
}

function describeNonnegativeIntError(value: string): string | null {
  if (value.trim() === '') return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return 'Enter a whole number.';
  if (num < 0) return 'Must be zero or more.';
  if (!Number.isInteger(num)) return 'Enter a whole number.';
  return null;
}

function describePositiveIntError(value: string): string | null {
  if (value.trim() === '') return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return 'Enter a whole number.';
  if (num <= 0) return 'Must be greater than zero.';
  if (!Number.isInteger(num)) return 'Enter a whole number.';
  return null;
}

function describePatternError(value: string): string | null {
  if (value.length > PATTERN_MAX_LEN) {
    return `Keep pattern under ${PATTERN_MAX_LEN} characters.`;
  }
  return null;
}

function describeReplacementError(value: string): string | null {
  if (value.length > REPLACEMENT_MAX_LEN) {
    return `Keep replacement under ${REPLACEMENT_MAX_LEN} characters.`;
  }
  return null;
}

export type Step9DataMigrationProps = IntakeShellRenderProps;

export function Step9DataMigration({
  stepDraft,
  onChange,
  issues,
}: Step9DataMigrationProps): JSX.Element {
  const errorsByPath = useMemo(() => groupIssuesByPath(issues), [issues]);
  const value = useMemo(() => readStep9(stepDraft), [stepDraft]);
  const { source, gingr, moego, pawloyalty, csv, sanitizationRules } = value;

  const serverErrorFor = (path: string): string | null => {
    const list = errorsByPath[path];
    return list && list.length > 0 ? list.join(' · ') : null;
  };

  const commit = (next: Step9Draft): void => {
    onChange(serializeStep9(next));
  };

  const onVariantChange = (next: SourceVariant): void => {
    if (next === source) return;
    // Clear variant-specific state to avoid stale keys after a switch.
    // The `z.discriminatedUnion('source', [...])` parse rejects any fragment
    // from a prior branch that doesn't match the new one.
    const cleaned: Step9Draft = {
      ...value,
      source: next,
      gingr:
        next === 'gingr'
          ? value.gingr
          : {
              gingrApiKey: '',
              gingrSubdomain: '',
              counts: { customers: '', pets: '', invoices: '', bookings: '' },
              loyaltyBackfill: '',
            },
      moego: next === 'moego' ? value.moego : { blobRefs: [] },
      pawloyalty: next === 'pawloyalty' ? value.pawloyalty : { blobRefs: [] },
      csv:
        next === 'csv'
          ? value.csv
          : { blobRefs: [], columnMapping: [] },
      sanitizationRules:
        next === 'none' || next === '' ? [] : value.sanitizationRules,
      confirmedNoMigration:
        next === 'none' ? value.confirmedNoMigration : false,
    };
    commit(cleaned);
  };

  const patchGingr = (patch: Partial<GingrDraft>): void => {
    commit({ ...value, gingr: { ...gingr, ...patch } });
  };

  const patchGingrCount = (
    key: keyof GingrDraft['counts'],
    next: string,
  ): void => {
    commit({
      ...value,
      gingr: { ...gingr, counts: { ...gingr.counts, [key]: next } },
    });
  };

  const patchBlobRefs = (variant: 'moego' | 'pawloyalty' | 'csv', rows: BlobRefRow[]): void => {
    if (variant === 'moego') commit({ ...value, moego: { blobRefs: rows } });
    else if (variant === 'pawloyalty') {
      commit({ ...value, pawloyalty: { blobRefs: rows } });
    } else {
      commit({ ...value, csv: { ...csv, blobRefs: rows } });
    }
  };

  const addBlobRef = (variant: 'moego' | 'pawloyalty' | 'csv'): void => {
    const current =
      variant === 'moego'
        ? moego.blobRefs
        : variant === 'pawloyalty'
          ? pawloyalty.blobRefs
          : csv.blobRefs;
    if (current.length >= MAX_BLOB_REF_ROWS) return;
    patchBlobRefs(variant, [
      ...current,
      { kind: '', blobKey: '', sizeBytes: '' },
    ]);
  };

  const removeBlobRef = (
    variant: 'moego' | 'pawloyalty' | 'csv',
    idx: number,
  ): void => {
    const current =
      variant === 'moego'
        ? moego.blobRefs
        : variant === 'pawloyalty'
          ? pawloyalty.blobRefs
          : csv.blobRefs;
    patchBlobRefs(
      variant,
      current.filter((_, i) => i !== idx),
    );
  };

  const patchBlobRefRow = (
    variant: 'moego' | 'pawloyalty' | 'csv',
    idx: number,
    patch: Partial<BlobRefRow>,
  ): void => {
    const current =
      variant === 'moego'
        ? moego.blobRefs
        : variant === 'pawloyalty'
          ? pawloyalty.blobRefs
          : csv.blobRefs;
    patchBlobRefs(
      variant,
      current.map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    );
  };

  const onBlobFilePick = (
    variant: 'moego' | 'pawloyalty' | 'csv',
    idx: number,
    file: File | null,
  ): void => {
    if (!file) return;
    patchBlobRefRow(variant, idx, {
      blobKey: `pending:${file.name}`,
      sizeBytes: String(file.size),
    });
  };

  const patchSanitization = (rows: SanitizationRuleRow[]): void => {
    commit({ ...value, sanitizationRules: rows });
  };

  const addSanitization = (): void => {
    if (sanitizationRules.length >= MAX_SANITIZATION_ROWS) return;
    patchSanitization([
      ...sanitizationRules,
      { pattern: '', replacement: '', scope: 'all' },
    ]);
  };

  const removeSanitization = (idx: number): void => {
    patchSanitization(sanitizationRules.filter((_, i) => i !== idx));
  };

  const patchSanitizationRow = (
    idx: number,
    patch: Partial<SanitizationRuleRow>,
  ): void => {
    patchSanitization(
      sanitizationRules.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    );
  };

  const patchColumnMapping = (rows: ColumnMappingRow[]): void => {
    commit({ ...value, csv: { ...csv, columnMapping: rows } });
  };

  const addColumnMapping = (): void => {
    if (csv.columnMapping.length >= MAX_COLUMN_MAPPING_ROWS) return;
    patchColumnMapping([...csv.columnMapping, { source: '', target: '' }]);
  };

  const removeColumnMapping = (idx: number): void => {
    patchColumnMapping(csv.columnMapping.filter((_, i) => i !== idx));
  };

  const patchColumnMappingRow = (
    idx: number,
    patch: Partial<ColumnMappingRow>,
  ): void => {
    patchColumnMapping(
      csv.columnMapping.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    );
  };

  const patchConfirmedNoMigration = (next: boolean): void => {
    commit({ ...value, confirmedNoMigration: next });
  };

  const sourceServerError = serverErrorFor('source');
  const rootServerError = serverErrorFor('_root');

  return (
    <form
      className="ferro-form ferro-intake__form"
      aria-label="Step 9 — Data migration source"
      noValidate
      onSubmit={(e) => e.preventDefault()}
    >
      <p className="ferro-muted">
        Tell us where your customer, pet, and booking history lives today.
        We’ll port it into your new Animal Lovers tenant when we provision
        — or you can start with a clean slate.
      </p>

      <section
        className="ferro-field"
        aria-labelledby="intake-step9-source-heading"
      >
        <h2
          id="intake-step9-source-heading"
          className="ferro-field__label"
        >
          Migration source
        </h2>
        <div
          role="radiogroup"
          aria-labelledby="intake-step9-source-heading"
          className="ferro-field ferro-intake__radioGroup"
          data-testid="intake-step9-source-group"
        >
          {SOURCE_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className="ferro-field ferro-intake__radio"
            >
              <input
                type="radio"
                name="step9-source"
                value={opt.value}
                checked={source === opt.value}
                onChange={() => onVariantChange(opt.value)}
                data-testid={`intake-step9-source-${opt.value}`}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
        {sourceServerError ? (
          <span
            role="alert"
            className="ferro-form__error"
            data-testid="intake-step9-source-servererror"
          >
            {sourceServerError}
          </span>
        ) : rootServerError ? (
          <span
            role="alert"
            className="ferro-form__error"
            data-testid="intake-step9-root-servererror"
          >
            {rootServerError}
          </span>
        ) : null}
      </section>

      {source === 'gingr' ? (
        <GingrFields
          draft={gingr}
          onPatch={patchGingr}
          onCountChange={patchGingrCount}
          serverErrorFor={serverErrorFor}
        />
      ) : null}

      {source === 'moego' || source === 'pawloyalty' || source === 'csv' ? (
        <BlobRefEditor
          variant={source}
          rows={
            source === 'moego'
              ? moego.blobRefs
              : source === 'pawloyalty'
                ? pawloyalty.blobRefs
                : csv.blobRefs
          }
          onAdd={() => addBlobRef(source)}
          onRemove={(idx) => removeBlobRef(source, idx)}
          onPatchRow={(idx, patch) => patchBlobRefRow(source, idx, patch)}
          onFilePick={(idx, file) => onBlobFilePick(source, idx, file)}
          serverErrorFor={serverErrorFor}
        />
      ) : null}

      {source === 'csv' ? (
        <ColumnMappingEditor
          rows={csv.columnMapping}
          onAdd={addColumnMapping}
          onRemove={removeColumnMapping}
          onPatch={patchColumnMappingRow}
          serverErrorFor={serverErrorFor}
        />
      ) : null}

      {source === 'gingr'
      || source === 'moego'
      || source === 'pawloyalty'
      || source === 'csv' ? (
        <SanitizationRulesEditor
          rows={sanitizationRules}
          onAdd={addSanitization}
          onRemove={removeSanitization}
          onPatch={patchSanitizationRow}
          serverErrorFor={serverErrorFor}
        />
      ) : null}

      {source === 'none' ? (
        <section
          className="ferro-field ferro-intake__conditional"
          data-testid="intake-step9-none-block"
        >
          <label className="ferro-field ferro-intake__checkbox">
            <input
              type="checkbox"
              checked={value.confirmedNoMigration}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                patchConfirmedNoMigration(e.target.checked)
              }
              aria-invalid={
                serverErrorFor('confirmedNoMigration') ? true : undefined
              }
              data-testid="intake-step9-confirm-no-migration"
            />
            <span className="ferro-field__label">
              I confirm we’re starting with a clean tenant — no historical data
              will be imported.
            </span>
          </label>
          {serverErrorFor('confirmedNoMigration') ? (
            <span role="alert" className="ferro-form__error">
              {serverErrorFor('confirmedNoMigration')}
            </span>
          ) : null}
        </section>
      ) : null}
    </form>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────

type GingrFieldsProps = {
  draft: GingrDraft;
  onPatch: (patch: Partial<GingrDraft>) => void;
  onCountChange: (key: keyof GingrDraft['counts'], next: string) => void;
  serverErrorFor: (path: string) => string | null;
};

function GingrFields({
  draft,
  onPatch,
  onCountChange,
  serverErrorFor,
}: GingrFieldsProps): JSX.Element {
  const apiKeyClientError = describeApiKeyError(draft.gingrApiKey);
  const apiKeyServerError = serverErrorFor('gingrApiKey');
  const subdomainClientError = describeSubdomainError(draft.gingrSubdomain);
  const subdomainServerError = serverErrorFor('gingrSubdomain');
  const customersClientError = describeNonnegativeIntError(draft.counts.customers);
  const customersServerError = serverErrorFor('recordCountsConfirmed.customers');
  const countsRootError = serverErrorFor('recordCountsConfirmed');
  const loyaltyServerError = serverErrorFor('loyaltyBackfill');
  const apiKeyError = apiKeyClientError ?? apiKeyServerError;
  const subdomainError = subdomainClientError ?? subdomainServerError;
  const customersError = customersClientError ?? customersServerError;

  return (
    <section
      className="ferro-field ferro-intake__conditional"
      aria-labelledby="intake-step9-gingr-heading"
      data-testid="intake-step9-gingr-block"
    >
      <h2 id="intake-step9-gingr-heading" className="ferro-field__label">
        Gingr connection
      </h2>
      <p className="ferro-muted">
        We’ll use your Gingr API key to copy owners, pets, bookings, and
        invoices. Generate one from Gingr’s “API Access” settings.
      </p>
      <label className="ferro-field">
        <span className="ferro-field__label">Gingr API key</span>
        <input
          type="text"
          className="ferro-field__input"
          value={draft.gingrApiKey}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onPatch({ gingrApiKey: e.target.value })
          }
          aria-invalid={apiKeyError ? true : undefined}
          aria-describedby={apiKeyError ? fieldErrorId('api-key') : undefined}
          data-testid="intake-step9-gingr-api-key"
        />
        {apiKeyError ? (
          <span
            id={fieldErrorId('api-key')}
            role="alert"
            className="ferro-form__error"
          >
            {apiKeyError}
          </span>
        ) : null}
      </label>
      <label className="ferro-field">
        <span className="ferro-field__label">Gingr subdomain</span>
        <input
          type="text"
          className="ferro-field__input"
          value={draft.gingrSubdomain}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onPatch({ gingrSubdomain: e.target.value.toLowerCase() })
          }
          placeholder="e.g. happy-tail"
          aria-invalid={subdomainError ? true : undefined}
          aria-describedby={
            subdomainError ? fieldErrorId('subdomain') : undefined
          }
          data-testid="intake-step9-gingr-subdomain"
        />
        {subdomainError ? (
          <span
            id={fieldErrorId('subdomain')}
            role="alert"
            className="ferro-form__error"
          >
            {subdomainError}
          </span>
        ) : null}
      </label>

      <fieldset
        className="ferro-field ferro-intake__countsGrid"
        data-testid="intake-step9-gingr-counts"
      >
        <legend className="ferro-field__label">Record counts (confirm)</legend>
        <p className="ferro-muted">
          Approximate numbers — helps us sanity-check the migration before we
          commit.
        </p>
        <CountField
          testId="intake-step9-gingr-count-customers"
          label="Customers"
          required
          value={draft.counts.customers}
          onChange={(v) => onCountChange('customers', v)}
          error={customersError}
        />
        <CountField
          testId="intake-step9-gingr-count-pets"
          label="Pets (optional)"
          value={draft.counts.pets}
          onChange={(v) => onCountChange('pets', v)}
          error={
            describeNonnegativeIntError(draft.counts.pets)
            ?? serverErrorFor('recordCountsConfirmed.pets')
          }
        />
        <CountField
          testId="intake-step9-gingr-count-bookings"
          label="Bookings (optional)"
          value={draft.counts.bookings}
          onChange={(v) => onCountChange('bookings', v)}
          error={
            describeNonnegativeIntError(draft.counts.bookings)
            ?? serverErrorFor('recordCountsConfirmed.bookings')
          }
        />
        <CountField
          testId="intake-step9-gingr-count-invoices"
          label="Invoices (optional)"
          value={draft.counts.invoices}
          onChange={(v) => onCountChange('invoices', v)}
          error={
            describeNonnegativeIntError(draft.counts.invoices)
            ?? serverErrorFor('recordCountsConfirmed.invoices')
          }
        />
        {countsRootError ? (
          <span
            role="alert"
            className="ferro-form__error"
            data-testid="intake-step9-gingr-counts-servererror"
          >
            {countsRootError}
          </span>
        ) : null}
      </fieldset>

      <label className="ferro-field">
        <span className="ferro-field__label">Loyalty point backfill</span>
        <select
          className="ferro-field__input"
          value={draft.loyaltyBackfill}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            onPatch({
              loyaltyBackfill: readLoyaltyBackfill(e.target.value),
            })
          }
          aria-invalid={loyaltyServerError ? true : undefined}
          data-testid="intake-step9-gingr-loyalty"
        >
          <option value="">Select…</option>
          {LOYALTY_BACKFILL_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {loyaltyServerError ? (
          <span role="alert" className="ferro-form__error">
            {loyaltyServerError}
          </span>
        ) : null}
      </label>
    </section>
  );
}

type CountFieldProps = {
  testId: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  required?: boolean;
  error?: string | null;
};

function CountField({
  testId,
  label,
  value,
  onChange,
  required,
  error,
}: CountFieldProps): JSX.Element {
  return (
    <label className="ferro-field">
      <span className="ferro-field__label">{label}</span>
      <input
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        required={required}
        className="ferro-field__input"
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.value)
        }
        aria-invalid={error ? true : undefined}
        data-testid={testId}
      />
      {error ? (
        <span role="alert" className="ferro-form__error">
          {error}
        </span>
      ) : null}
    </label>
  );
}

type BlobRefEditorProps = {
  variant: 'moego' | 'pawloyalty' | 'csv';
  rows: ReadonlyArray<BlobRefRow>;
  onAdd: () => void;
  onRemove: (idx: number) => void;
  onPatchRow: (idx: number, patch: Partial<BlobRefRow>) => void;
  onFilePick: (idx: number, file: File | null) => void;
  serverErrorFor: (path: string) => string | null;
};

function BlobRefEditor({
  variant,
  rows,
  onAdd,
  onRemove,
  onPatchRow,
  onFilePick,
  serverErrorFor,
}: BlobRefEditorProps): JSX.Element {
  const kindOptions = variant === 'csv' ? CSV_KINDS_WITH_STAFF : CSV_KIND_OPTIONS;
  const rootError = serverErrorFor('csvBlobRefs');
  return (
    <section
      className="ferro-field ferro-intake__conditional"
      aria-labelledby="intake-step9-blobs-heading"
      data-testid="intake-step9-blobs-block"
    >
      <h2 id="intake-step9-blobs-heading" className="ferro-field__label">
        Upload your CSV exports
      </h2>
      <p
        className="ferro-muted"
        data-testid="intake-step9-blobs-deferred-notice"
      >
        Automatic upload is in limited release. For now, pick each CSV file
        and we’ll track the filename and size; the actual transfer happens
        on our call before provisioning.
      </p>
      {rootError ? (
        <span
          role="alert"
          className="ferro-form__error"
          data-testid="intake-step9-blobs-servererror"
        >
          {rootError}
        </span>
      ) : null}
      <ul
        className="ferro-intake__blobRefsList"
        data-testid="intake-step9-blobs-list"
      >
        {rows.map((row, idx) => {
          const kindError = serverErrorFor(`csvBlobRefs.${idx}.kind`);
          const keyError = serverErrorFor(`csvBlobRefs.${idx}.blobKey`);
          const sizeServerError = serverErrorFor(`csvBlobRefs.${idx}.sizeBytes`);
          const sizeClientError = describePositiveIntError(row.sizeBytes);
          const sizeError = sizeClientError ?? sizeServerError;
          return (
            <li
              key={idx}
              className="ferro-intake__blobRefRow"
              data-testid={`intake-step9-blob-row-${idx}`}
            >
              <label className="ferro-field">
                <span className="ferro-field__label">Record type</span>
                <select
                  className="ferro-field__input"
                  value={row.kind}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    onPatchRow(idx, {
                      kind: readCsvKind(e.target.value, variant === 'csv'),
                    })
                  }
                  aria-invalid={kindError ? true : undefined}
                  data-testid={`intake-step9-blob-row-${idx}-kind`}
                >
                  <option value="">Select…</option>
                  {kindOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {kindError ? (
                  <span role="alert" className="ferro-form__error">
                    {kindError}
                  </span>
                ) : null}
              </label>
              <label className="ferro-field">
                <span className="ferro-field__label">File</span>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="ferro-field__input"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const file = e.target.files?.[0] ?? null;
                    onFilePick(idx, file);
                  }}
                  data-testid={`intake-step9-blob-row-${idx}-file`}
                />
              </label>
              <label className="ferro-field">
                <span className="ferro-field__label">Blob key</span>
                <input
                  type="text"
                  className="ferro-field__input"
                  value={row.blobKey}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    onPatchRow(idx, { blobKey: e.target.value })
                  }
                  placeholder="pending:customers.csv"
                  aria-invalid={keyError ? true : undefined}
                  data-testid={`intake-step9-blob-row-${idx}-key`}
                />
                {keyError ? (
                  <span role="alert" className="ferro-form__error">
                    {keyError}
                  </span>
                ) : null}
              </label>
              <label className="ferro-field">
                <span className="ferro-field__label">Size (bytes)</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  className="ferro-field__input"
                  value={row.sizeBytes}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    onPatchRow(idx, { sizeBytes: e.target.value })
                  }
                  aria-invalid={sizeError ? true : undefined}
                  data-testid={`intake-step9-blob-row-${idx}-size`}
                />
                {sizeError ? (
                  <span role="alert" className="ferro-form__error">
                    {sizeError}
                  </span>
                ) : null}
              </label>
              <button
                type="button"
                className="ferro-btn ferro-btn--secondary"
                onClick={() => onRemove(idx)}
                data-testid={`intake-step9-blob-row-${idx}-remove`}
              >
                Remove
              </button>
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        className="ferro-btn ferro-btn--secondary"
        onClick={onAdd}
        disabled={rows.length >= MAX_BLOB_REF_ROWS}
        data-testid="intake-step9-blobs-add"
      >
        Add CSV file
      </button>
    </section>
  );
}

type ColumnMappingEditorProps = {
  rows: ReadonlyArray<ColumnMappingRow>;
  onAdd: () => void;
  onRemove: (idx: number) => void;
  onPatch: (idx: number, patch: Partial<ColumnMappingRow>) => void;
  serverErrorFor: (path: string) => string | null;
};

function ColumnMappingEditor({
  rows,
  onAdd,
  onRemove,
  onPatch,
  serverErrorFor,
}: ColumnMappingEditorProps): JSX.Element {
  const rootError = serverErrorFor('columnMapping');
  return (
    <section
      className="ferro-field ferro-intake__conditional"
      aria-labelledby="intake-step9-mapping-heading"
      data-testid="intake-step9-mapping-block"
    >
      <h2
        id="intake-step9-mapping-heading"
        className="ferro-field__label"
      >
        Column mapping (optional)
      </h2>
      <p className="ferro-muted">
        Tell us how your CSV columns map to Animal Lovers fields. Leave blank
        if your CSVs already use our standard column names.
      </p>
      {rootError ? (
        <span
          role="alert"
          className="ferro-form__error"
          data-testid="intake-step9-mapping-servererror"
        >
          {rootError}
        </span>
      ) : null}
      <ul
        className="ferro-intake__mappingList"
        data-testid="intake-step9-mapping-list"
      >
        {rows.map((row, idx) => (
          <li
            key={idx}
            className="ferro-intake__mappingRow"
            data-testid={`intake-step9-mapping-row-${idx}`}
          >
            <label className="ferro-field">
              <span className="ferro-field__label">Your column</span>
              <input
                type="text"
                className="ferro-field__input"
                value={row.source}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onPatch(idx, { source: e.target.value })
                }
                data-testid={`intake-step9-mapping-row-${idx}-source`}
              />
            </label>
            <label className="ferro-field">
              <span className="ferro-field__label">Our field</span>
              <input
                type="text"
                className="ferro-field__input"
                value={row.target}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onPatch(idx, { target: e.target.value })
                }
                data-testid={`intake-step9-mapping-row-${idx}-target`}
              />
            </label>
            <button
              type="button"
              className="ferro-btn ferro-btn--secondary"
              onClick={() => onRemove(idx)}
              data-testid={`intake-step9-mapping-row-${idx}-remove`}
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="ferro-btn ferro-btn--secondary"
        onClick={onAdd}
        disabled={rows.length >= MAX_COLUMN_MAPPING_ROWS}
        data-testid="intake-step9-mapping-add"
      >
        Add column mapping
      </button>
    </section>
  );
}

type SanitizationRulesEditorProps = {
  rows: ReadonlyArray<SanitizationRuleRow>;
  onAdd: () => void;
  onRemove: (idx: number) => void;
  onPatch: (idx: number, patch: Partial<SanitizationRuleRow>) => void;
  serverErrorFor: (path: string) => string | null;
};

function SanitizationRulesEditor({
  rows,
  onAdd,
  onRemove,
  onPatch,
  serverErrorFor,
}: SanitizationRulesEditorProps): JSX.Element {
  const rootError = serverErrorFor('sanitizationRules');
  return (
    <section
      className="ferro-field ferro-intake__conditional"
      aria-labelledby="intake-step9-sanitization-heading"
      data-testid="intake-step9-sanitization-block"
    >
      <h2
        id="intake-step9-sanitization-heading"
        className="ferro-field__label"
      >
        Sanitization rules (optional)
      </h2>
      <p className="ferro-muted">
        Swap a literal string in imported data and/or UI. Useful for stripping
        legacy branding or renaming services before they go live.
      </p>
      {rootError ? (
        <span
          role="alert"
          className="ferro-form__error"
          data-testid="intake-step9-sanitization-servererror"
        >
          {rootError}
        </span>
      ) : null}
      <ul
        className="ferro-intake__sanitizationList"
        data-testid="intake-step9-sanitization-list"
      >
        {rows.map((row, idx) => {
          const patternClientError = describePatternError(row.pattern);
          const patternServerError = serverErrorFor(
            `sanitizationRules.${idx}.pattern`,
          );
          const patternError = patternClientError ?? patternServerError;
          const replacementClientError = describeReplacementError(
            row.replacement,
          );
          const replacementServerError = serverErrorFor(
            `sanitizationRules.${idx}.replacement`,
          );
          const replacementError = replacementClientError ?? replacementServerError;
          return (
            <li
              key={idx}
              className="ferro-intake__sanitizationRow"
              data-testid={`intake-step9-sanitization-row-${idx}`}
            >
              <label className="ferro-field">
                <span className="ferro-field__label">Pattern</span>
                <input
                  type="text"
                  className="ferro-field__input"
                  value={row.pattern}
                  maxLength={PATTERN_MAX_LEN}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    onPatch(idx, { pattern: e.target.value })
                  }
                  aria-invalid={patternError ? true : undefined}
                  data-testid={`intake-step9-sanitization-row-${idx}-pattern`}
                />
                {patternError ? (
                  <span role="alert" className="ferro-form__error">
                    {patternError}
                  </span>
                ) : null}
              </label>
              <label className="ferro-field">
                <span className="ferro-field__label">Replacement</span>
                <input
                  type="text"
                  className="ferro-field__input"
                  value={row.replacement}
                  maxLength={REPLACEMENT_MAX_LEN}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    onPatch(idx, { replacement: e.target.value })
                  }
                  aria-invalid={replacementError ? true : undefined}
                  data-testid={`intake-step9-sanitization-row-${idx}-replacement`}
                />
                {replacementError ? (
                  <span role="alert" className="ferro-form__error">
                    {replacementError}
                  </span>
                ) : null}
              </label>
              <label className="ferro-field">
                <span className="ferro-field__label">Scope</span>
                <select
                  className="ferro-field__input"
                  value={row.scope}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    onPatch(idx, { scope: readScope(e.target.value) })
                  }
                  data-testid={`intake-step9-sanitization-row-${idx}-scope`}
                >
                  {SANITIZATION_SCOPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="ferro-btn ferro-btn--secondary"
                onClick={() => onRemove(idx)}
                data-testid={`intake-step9-sanitization-row-${idx}-remove`}
              >
                Remove
              </button>
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        className="ferro-btn ferro-btn--secondary"
        onClick={onAdd}
        disabled={rows.length >= MAX_SANITIZATION_ROWS}
        data-testid="intake-step9-sanitization-add"
      >
        Add sanitization rule
      </button>
    </section>
  );
}
